import { ChangeEvent, FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { useCreateMapMutation, useUpdateMapMutation } from "@/pages/Private/redux/map/map.api";

import { ERROR_TYPE, getAllErrors, renderErrorMessages, useSearch } from "@/utils";

import { ScrapeLevel } from "@/enum/job.enum";

import { Button } from "@/components/Button/Button";

import { InputField } from "@/components/InputField/InputField";

import { ButtonColor } from "@/components/Button/types";

import { LinkedinParams } from "@/pages/Private/pages/LiPeople/components/LinkedinTagAutocomplete";
import { AutoCompleteItem, AutoComplete } from "@/components/AutoComplete/AutoComplete";
import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";

import { List } from "../../List/schema/list";
import { CreateMap, CreateMapSchema, Map } from "../schema/map";

interface CreateMapFormProps {
	map?: Map | null;
	list: List;
	handleSelect?: (map: Map | null) => void;
	handleDuplicate?: (map: Map | null) => void;
}

export const CreateMapForm: FunctionComponent<CreateMapFormProps> = ({
	map,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const initialState = {
		name: "",
		query: "",
		country: "",
		city: "",
		mapScrapeType: map?.mapScrapeType || (list.scrapeLevel as ScrapeLevel),
		repeatDaily: false,
	};

	const { t } = useTranslation();

	const [update] = useUpdateMapMutation();
	const [createMap, { error, isLoading }] = useCreateMapMutation();

	const [currentFormState, setCurrentFormState] = useState<Partial<CreateMap>>(initialState);

	const {
		handleSubmit,
		setValue,
		formState: { errors },
		reset,
	} = useForm<CreateMap>({
		defaultValues: {
			name: map?.name ?? "",
			query: map?.query ?? "",
			country: map?.country ?? "",
			city: map?.city ?? "",
			mapScrapeType: map?.mapScrapeType || (list.scrapeLevel as ScrapeLevel),
			repeatDaily: false,
		},
		resolver: zodResolver(CreateMapSchema),
	});

	const [fetchTags, { isLoading: isLoadingTags }] = useLazyGetFilterByPropertyQuery();
	const [cityOptions, setCityOptions] = useState<LinkedinParams[]>([]);
	const [countryOptions, setCountryOptions] = useState<LinkedinParams[]>([]);

	const {
		query: cityQuery,
		searchValue: citySearchValue,
		setSearchValue: setCitySearchValue,
	} = useSearch();
	const {
		query: countryQuery,
		searchValue: countrySearchValue,
		setSearchValue: setCountrySearchValue,
	} = useSearch();

	useEffect(() => {
		if (cityQuery) {
			fetchTags({ propertyName: "city", searchTerm: cityQuery }).then((results) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const optionValues = results?.data?.data?.map((res: any) => {
					return {
						name: res.value,
						id: res.id,
					};
				});

				if (optionValues) {
					setCityOptions([...optionValues]);
				}
			});
		}
	}, [cityQuery, fetchTags]);

	useEffect(() => {
		if (countryQuery) {
			fetchTags({ propertyName: "country", searchTerm: countryQuery }).then((results) => {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const optionValues = results?.data?.data?.map((res: any) => {
					return {
						name: res.value,
						id: res.id,
					};
				});

				if (optionValues) {
					setCountryOptions([...optionValues]);
				}
			});
		}
	}, [countryQuery, fetchTags]);

	const handleSaveFormState = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(value: any, name: keyof CreateMap) => {
			setCurrentFormState({
				...currentFormState,
				[name]: value,
			});

			setValue(name, value, {
				shouldDirty: true,
				shouldValidate: true,
			});
		},
		[currentFormState, setValue]
	);

	useEffect(() => {
		if (map) {
			const formState = {
				name: map?.name ?? "",
				query: map?.query ?? "",
				country: map?.country ?? "",
				city: map?.city ?? "",
				mapScrapeType: map?.mapScrapeType || (list.scrapeLevel as ScrapeLevel),
				repeatDaily: false,
			};

			if (handleDuplicate) {
				reset({
					...formState,
				});
				setCurrentFormState({
					...formState,
				});
			} else {
				reset({
					id: map.id,
					...formState,
				});
				setCurrentFormState({
					...formState,
					id: map?.id,
				});
			}
		} else {
			reset();
		}
	}, [handleDuplicate, list.scrapeLevel, map, reset]);

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	useEffect(() => {
		if (list.scrapeLevel) {
			setValue("mapScrapeType", list.scrapeLevel as ScrapeLevel);
			setValue("repeatDaily", false);
		}
	}, [list.scrapeLevel, setValue]);

	const onSubmit = async (values: CreateMap) => {
		try {
			if (values.id && !handleDuplicate) {
				await update({ id: values.id, ...values, listId: list.id }).unwrap();
			} else {
				await createMap({ ...values, listId: list.id }).unwrap();
			}

			reset();
			handleSelect?.(null);
			handleDuplicate?.(null);
		} catch (err) {
			console.error(err);
		}
	};

	const buttonTitle =
		handleDuplicate && map?.id
			? t("basics.copyCriteria")
			: map?.id
				? t("basics.save")
				: t("basics.addCriteria");

	return (
		<div className="p-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
			<div className="text-md mb-5">Add a source</div>
			<div className="flex flex-row w-full mb-1">
				<InputField
					error={!!errors.name?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						handleSaveFormState(event.target.value, "name");
					}}
					label={"Source name"}
					name={"name"}
					placeholder={"Doctors in Berlin, Germany"}
					showError={!!errors.name?.message}
					value={currentFormState?.name ?? ""}
				/>
			</div>

			<div className="flex flex-row w-full mb-1">
				<InputField
					error={!!errors.query?.message}
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						handleSaveFormState(event.target.value, "query");
					}}
					label={"Keyword"}
					name={"query"}
					placeholder={"e.g. Physical Therapy, Doctor, Hotels etc."}
					showError={!!errors.query?.message}
					value={currentFormState?.query ?? ""}
				/>
			</div>

			<div className="flex flex-row w-full mb-4">
				<AutoComplete
					data={[
						...(countryOptions
							?.filter((r) => r.id !== "" && r.name !== countrySearchValue)
							?.map((r) => ({
								id: r.id,
								title: r.name,
							})) ?? []),
						{ id: countrySearchValue ?? "", title: countrySearchValue ?? "" },
					]}
					error={!!errors.country?.message}
					handleReset={() => {
						setCountrySearchValue("");
						setCurrentFormState({
							...currentFormState,
							country: undefined,
						});
					}}
					handleSearch={(value: string) => setCountrySearchValue(value)}
					handleSelect={async (item?: AutoCompleteItem) => {
						if (item) {
							handleSaveFormState(item.title, "country");
							setCountrySearchValue("");
						} else {
							handleSaveFormState(undefined, "country");
						}
					}}
					isLoading={isLoadingTags}
					label="Country"
					placeholder="e.g. Germany, USA, etc."
					selectedItem={currentFormState?.country || ""}
				/>
			</div>

			<div className="flex flex-row w-full mb-4">
				<AutoComplete
					data={[
						...(cityOptions
							?.filter((r) => r.id !== "" && r.name !== citySearchValue)
							?.map((r) => ({
								id: r.id,
								title: r.name,
							})) ?? []),
						{ id: citySearchValue ?? "", title: citySearchValue ?? "" },
					]}
					error={!!errors.city?.message}
					handleReset={() => {
						setCitySearchValue("");
						handleSaveFormState(undefined, "city");
					}}
					handleSearch={(value: string) => setCitySearchValue(value)}
					handleSelect={async (item?: AutoCompleteItem) => {
						if (item) {
							handleSaveFormState(item.title, "city");
							setCitySearchValue("");
						} else {
							handleSaveFormState(undefined, "city");
						}
					}}
					isLoading={isLoadingTags}
					label="City"
					placeholder="e.g. Berlin, Munich, etc."
					selectedItem={currentFormState?.city || ""}
				/>
			</div>

			{getAllErrors(error, formErrors).length ? (
				<div className="flex flex-row gap-4 w-full mb-4">
					{renderErrorMessages(getAllErrors(error, formErrors))}
				</div>
			) : null}

			<div className="flex gap-4 w-full justify-end">
				{handleSelect && (
					<div className="flex max-w-[100px] w-full">
						<Button
							className="w-full"
							color={ButtonColor.ACTION_SECONDARY}
							title="cancel"
							onClick={() => {
								handleSelect(null);
								handleDuplicate?.(null);
								reset(initialState);
								setCurrentFormState(initialState);
							}}
						/>
					</div>
				)}

				<div className="flex max-w-[150px] w-full">
					<Button
						className="w-full"
						isLoading={isLoading}
						title={buttonTitle}
						onClick={handleSubmit(onSubmit)}
					/>
				</div>
			</div>
		</div>
	);
};
